/**
 * POLYCAM UPLOAD COMPONENT
 * 
 * Upload and process Polycam Spatial Report PDFs
 * Shows processing status and allows room classification review
 */

import React, { useState, useRef } from 'react';
import { 
  Smartphone, 
  Upload, 
  FileText, 
  Check, 
  AlertCircle, 
  Loader2, 
  X,
  ChevronDown,
  ChevronUp,
  MapPin,
  Home,
  Ruler,
  Package
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Room type options for reclassification
const ROOM_TYPES = [
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'living', label: 'Living Room' },
  { value: 'dining', label: 'Dining Room' },
  { value: 'office', label: 'Office/Study' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'mudroom', label: 'Mudroom' },
  { value: 'garage', label: 'Garage' },
  { value: 'basement', label: 'Basement' },
  { value: 'attic', label: 'Attic' },
  { value: 'hall', label: 'Hall/Entry' },
  { value: 'closet', label: 'Closet' },
  { value: 'pantry', label: 'Pantry' },
  { value: 'custom', label: 'Custom' },
];

const PolycamUpload = ({ 
  disabled = false, 
  fieldVisitId,
  onImportComplete,
}) => {
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState('idle'); // idle, uploading, reviewing, confirming, complete, error
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedData, setParsedData] = useState(null);
  const [importId, setImportId] = useState(null);
  const [roomClassifications, setRoomClassifications] = useState({});
  const [expandedRooms, setExpandedRooms] = useState({});

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file. Only Polycam Spatial Report PDFs are supported.');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('File is too large. Maximum size is 20MB.');
      return;
    }

    setStatus('uploading');
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisitId}/polycam/upload`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData,
        }
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload file');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to parse PDF');
      }

      setParsedData(data);
      setImportId(data.import_id);
      
      // Initialize room classifications from parsed data
      const classifications = {};
      data.preview?.areas?.forEach(area => {
        if (area.needs_type_review) {
          classifications[area.label.toLowerCase()] = area.type;
        }
      });
      setRoomClassifications(classifications);
      
      setStatus('reviewing');

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
      setStatus('error');
    }
  };

  const handleClassificationChange = (roomName, newType) => {
    setRoomClassifications(prev => ({
      ...prev,
      [roomName.toLowerCase()]: newType,
    }));
  };

  const toggleRoomExpanded = (areaId) => {
    setExpandedRooms(prev => ({
      ...prev,
      [areaId]: !prev[areaId],
    }));
  };

  const handleConfirmImport = async () => {
    if (!importId) return;

    setStatus('confirming');
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE}/api/field-visits/${fieldVisitId}/polycam/confirm`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            import_id: importId,
            room_classifications: roomClassifications,
            options: {
              skip_small_rooms: false,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to confirm import');
      }

      const result = await response.json();
      
      setStatus('complete');
      
      // Notify parent component
      if (onImportComplete) {
        onImportComplete({
          areas_imported: result.areas_imported,
          fixtures_imported: result.fixtures_imported,
        });
      }

    } catch (err) {
      console.error('Confirm error:', err);
      setError(err.message || 'Failed to confirm import');
      setStatus('error');
    }
  };

  const handleCancel = () => {
    setStatus('idle');
    setError(null);
    setParsedData(null);
    setImportId(null);
    setRoomClassifications({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Render based on status
  if (status === 'idle' || status === 'error') {
    return (
      <div className="p-6 border-2 border-dashed border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Smartphone className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Polycam LiDAR Import</h4>
          <p className="text-sm text-slate-400 mb-4">
            Upload a Polycam Spatial Report PDF to auto-populate room dimensions and fixtures
          </p>
          
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-left">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-400">{error}</p>
              </div>
            </div>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            disabled={disabled || !fieldVisitId}
            className="hidden"
            data-testid="polycam-file-input"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || !fieldVisitId}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded 
              flex items-center gap-2 mx-auto disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="polycam-upload-button"
          >
            <Upload className="w-4 h-4" />
            Upload PDF
          </button>
          
          <p className="text-xs text-slate-500 mt-3">
            Export your scan from Polycam as a "Spatial Report (PDF)"
          </p>
        </div>
      </div>
    );
  }

  if (status === 'uploading') {
    return (
      <div className="p-6 border-2 border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 mx-auto mb-3 animate-spin" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Processing PDF...</h4>
          <p className="text-sm text-slate-400 mb-4">
            Extracting room dimensions and fixtures
          </p>
          
          {/* Progress bar */}
          <div className="max-w-xs mx-auto">
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-emerald-500 transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">{uploadProgress}%</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'reviewing' && parsedData) {
    const areas = parsedData.preview?.areas || [];
    const roomsNeedingReview = areas.filter(a => a.needs_type_review);

    return (
      <div className="border-2 border-emerald-500/30 rounded-lg bg-slate-800/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 bg-emerald-500/10 border-b border-emerald-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-emerald-400" />
              <div>
                <h4 className="text-lg font-medium text-slate-200">Polycam Import Preview</h4>
                <p className="text-sm text-slate-400">
                  {parsedData.rooms_detected} rooms detected • {parsedData.fixtures_detected} fixtures
                </p>
              </div>
            </div>
            <button
              onClick={handleCancel}
              className="p-2 hover:bg-slate-700 rounded"
              title="Cancel import"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Warnings */}
        {parsedData.warnings?.length > 0 && (
          <div className="p-3 bg-amber-500/10 border-b border-amber-500/30">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5" />
              <div className="text-sm text-amber-400">
                {parsedData.warnings.map((w, i) => (
                  <p key={i}>{w}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Room Classification */}
        {roomsNeedingReview.length > 0 && (
          <div className="p-4 border-b border-slate-700">
            <h5 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {roomsNeedingReview.length} room(s) need classification
            </h5>
            <div className="space-y-2">
              {roomsNeedingReview.map(area => (
                <div key={area.area_id} className="flex items-center gap-3 p-2 bg-slate-800 rounded">
                  <span className="text-slate-300 flex-1">{area.label}</span>
                  <select
                    value={roomClassifications[area.label.toLowerCase()] || area.type}
                    onChange={(e) => handleClassificationChange(area.label, e.target.value)}
                    className="px-3 py-1.5 bg-slate-900 border border-slate-600 text-slate-200 rounded text-sm"
                  >
                    {ROOM_TYPES.map(rt => (
                      <option key={rt.value} value={rt.value}>{rt.label}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Room List */}
        <div className="p-4 max-h-80 overflow-y-auto">
          <h5 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Rooms to Import
          </h5>
          <div className="space-y-2">
            {areas.map(area => (
              <div 
                key={area.area_id}
                className="border border-slate-700 rounded overflow-hidden"
              >
                <button
                  onClick={() => toggleRoomExpanded(area.area_id)}
                  className="w-full p-3 flex items-center justify-between hover:bg-slate-800/50"
                >
                  <div className="flex items-center gap-3">
                    <Home className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-200 font-medium">{area.label}</span>
                    <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-700 rounded">
                      {area.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {area.dimensions?.floor_sf && (
                      <span className="text-sm text-slate-400">
                        {Math.round(area.dimensions.floor_sf)} SF
                      </span>
                    )}
                    {expandedRooms[area.area_id] ? (
                      <ChevronUp className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    )}
                  </div>
                </button>
                
                {expandedRooms[area.area_id] && (
                  <div className="px-3 pb-3 pt-1 border-t border-slate-700 bg-slate-800/30">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {area.dimensions && (
                        <>
                          <div className="flex items-center gap-2 text-slate-400">
                            <Ruler className="w-3 h-3" />
                            <span>
                              {area.dimensions.length_ft}'{area.dimensions.length_in}" × {area.dimensions.width_ft}'{area.dimensions.width_in}"
                            </span>
                          </div>
                          <div className="text-slate-400">
                            Ceiling: {area.dimensions.ceiling_height_ft}'{area.dimensions.ceiling_height_in}"
                          </div>
                        </>
                      )}
                      {Object.keys(area.fixtures || {}).length > 0 && (
                        <div className="col-span-2 flex items-center gap-2 text-emerald-400 mt-1">
                          <Package className="w-3 h-3" />
                          <span>{Object.keys(area.fixtures).length} fixtures detected</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 bg-slate-800/50 border-t border-slate-700 flex items-center justify-between">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-slate-400 hover:text-slate-200"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmImport}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded
              flex items-center gap-2"
            data-testid="polycam-confirm-button"
          >
            <Check className="w-4 h-4" />
            Import {areas.length} Rooms
          </button>
        </div>
      </div>
    );
  }

  if (status === 'confirming') {
    return (
      <div className="p-6 border-2 border-slate-700 rounded-lg bg-slate-800/30">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 mx-auto mb-3 animate-spin" />
          <h4 className="text-lg font-medium text-slate-200 mb-2">Importing rooms...</h4>
          <p className="text-sm text-slate-400">
            Adding rooms and fixtures to your field visit
          </p>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div className="p-6 border-2 border-emerald-500/30 rounded-lg bg-emerald-500/5">
        <div className="text-center">
          <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Check className="w-6 h-6 text-emerald-400" />
          </div>
          <h4 className="text-lg font-medium text-emerald-400 mb-2">Import Complete!</h4>
          <p className="text-sm text-slate-400 mb-4">
            Rooms and fixtures have been added to your field visit.
            Review each room below to confirm details.
          </p>
          <button
            onClick={handleCancel}
            className="text-sm text-emerald-400 hover:text-emerald-300"
          >
            Import another PDF
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default PolycamUpload;
